import { identifyUser as identifyLogRocketUser } from '@shared/plugins/logRocket';
import { useUserStore } from '@shared/stores/user';
import { useLDClient } from 'launchdarkly-vue-client-sdk';
import { storeToRefs } from 'pinia';
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw
} from 'vue-router';
import { getCurrentUser } from 'vuefire';

import contractors from './contractors';
import externalRoutes from './externalRoutes';
import jobs from './jobs';
import sharedRoutes from './sharedRoutes';

let allRoutes = [] as RouteRecordRaw[];
allRoutes = allRoutes.concat(contractors, jobs, sharedRoutes, externalRoutes);

const router = createRouter({
  history: createWebHistory(),
  routes: allRoutes
});

router.beforeEach(async (to) => {
  const userStore = useUserStore();
  const { currentS3User } = storeToRefs(userStore);
  const ldClient = useLDClient();
  const currentFireBaseUser = await getCurrentUser();
  if (!currentFireBaseUser) {
    if (to.meta.requiresAuth) {
      // if the user is not logged in, redirect to the login page
      return {
        path: '/login',
        query: {
          // we keep the current path in the query so we can
          // redirect to it after login with
          // `router.push(route.query.redirect || '/')`
          redirect: to.fullPath
        }
      };
    }
    if (to.meta.requiresGuestAuth) {
      // If user is not logged in, redirect to reauth guest user page
      return {
        path: '/guest-logout'
      };
    }
    return;
  }
  userStore.setCurrentAuthenticatedUser(currentFireBaseUser);
  if (currentS3User.value?.email !== currentFireBaseUser.email) {
    await userStore.fetchCurrentS3User();
  }
  if (to.meta.requiresAuth && currentFireBaseUser.email) {
    identifyLogRocketUser(currentFireBaseUser.email);
    try {
      await ldClient.identify({
        kind: 'user',
        key: currentFireBaseUser.uid,
        name: currentFireBaseUser.displayName || 'unknown',
        email: currentFireBaseUser.email
      });
    } catch (e) {
      // we will swallow these errors -- user will get default flag values
    }
  }
});

router.afterEach((to, from, failure) => {
  if (failure) return false;

  if (to.meta.title)
    window.document.title = `${to.meta.title || to.name} | Sealed`;
});

router.onError((error) => {
  // On deploy if a dynamic import changes the hash will change causing the
  // dynmaic import to fail. This will reload the page and the new hash will
  // be correct.
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    window.location.reload();
  }
});

export default router;
